"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.serviceToDesign = serviceToDesign;
/**
 * `serviceToDesign` — the SEED of the free-form pass designer (spec §5). Produces
 * a fully-editable default `PassDesign` from a service + flight + chosen style:
 * reuses `serviceToContent` for the field content, distributes its flat `fields`
 * into the style's groups (mirroring the wallet-apple per-style adapter), applies
 * default Insurwiz-navy colours, default bundled icon/logo image refs, and seeds
 * flight `semantics` for `boardingPass`. The operator's overrides are then merged
 * over this seed via `mergePassDesign`.
 */
const airportLocations_js_1 = require("./airportLocations.js");
const airportTimeZones_js_1 = require("./airportTimeZones.js");
const flightStatusText_js_1 = require("./flightStatusText.js");
const serviceToContent_js_1 = require("./serviceToContent.js");
/** Default Insurwiz navy + white text — same constants as the wallet-apple adapter. */
const DEFAULT_BACKGROUND = 'rgb(0, 51, 102)';
const WHITE = 'rgb(255, 255, 255)';
/** Bundled-default image key bases (spec §2/§5 convention: s3KeyBase = @2x key sans `.png`). */
const DEFAULT_ICON_KEY = 'defaults/icon';
const DEFAULT_LOGO_KEY = 'defaults/logo';
/** Apple flight semantics from flight context — boardingPass native tracking triple. */
function seedSemantics(flight) {
    const flightNumber = Number.parseInt(flight.number, 10);
    return {
        airlineCode: flight.carrier,
        ...(Number.isNaN(flightNumber) ? {} : { flightNumber }),
        originalDepartureDate: flight.scheduledDeparture,
        ...(flight.gate !== null ? { departureGate: flight.gate } : {}),
        ...(flight.terminal !== null ? { departureTerminal: flight.terminal } : {}),
        departureAirportCode: flight.departureAirport,
        destinationAirportCode: flight.arrivalAirport,
    };
}
/** Departure-airport geofence radius (metres) — matches realAppleMinter's
 *  AIRPORT_GEOFENCE_RADIUS_M so the seed and the no-template mint agree. */
const AIRPORT_GEOFENCE_RADIUS_M = 5000;
/**
 * Seed `relevance` from the flight context: the departure-airport geofence
 * (lock-screen relevance near the airport) + the flight window dates.
 * `buildLivePatch` forwards this verbatim at mint, and the GET-one preview
 * renders the same shape, so design-time and mint can never disagree.
 *
 * `relevantDate` is seeded ONLY on the time-anchored styles (boardingPass /
 * eventTicket — Apple's pass-relevance semantics); on coupon/storeCard iOS
 * ignores it, so seeding it would ship a fresh design with a Wallet
 * time-warning generated by the seed itself. Locations + expirationDate stay
 * on every style.
 */
function seedRelevance(flight, style) {
    const locations = (0, airportLocations_js_1.buildAirportLocations)(flight.departureAirport, null).map((l) => ({ latitude: l.latitude, longitude: l.longitude }));
    // Expiry: arrival + 6h, NOT arrival. Wallet auto-hides an expired pass, and
    // an expiry AT scheduled arrival hid the pass at the exact moment the
    // passenger wanted the landing status on it (W6 2247, 2026-08-30 01:50 —
    // the 'Elindult' patch and the arrival window both landed on an already
    // hidden pass). Six hours covers a late landing plus the "did it arrive"
    // glance; an unparseable arrival keeps the raw value (validate flags it).
    const arrivalMs = Date.parse(flight.scheduledArrival);
    const expirationDate = Number.isNaN(arrivalMs)
        ? flight.scheduledArrival
        : new Date(arrivalMs + 6 * 60 * 60 * 1000).toISOString();
    return {
        locations,
        maxDistance: AIRPORT_GEOFENCE_RADIUS_M,
        ...(style === 'boardingPass' || style === 'eventTicket'
            ? { relevantDate: flight.scheduledDeparture }
            : {}),
        expirationDate,
    };
}
function serviceToDesign(service, flight, style) {
    const content = (0, serviceToContent_js_1.serviceToContent)(service, flight);
    // service fields pool → PassDesignField[] (drop the empty-label guard: serviceToContent
    // labels are always non-empty, but PassDesignField.label is optional so we pass it through).
    const serviceFields = content.fields.map((f) => ({
        key: f.key,
        label: f.label,
        value: f.value,
    }));
    const chrome = {
        images: {
            icon: { s3KeyBase: DEFAULT_ICON_KEY },
            logo: { s3KeyBase: DEFAULT_LOGO_KEY },
        },
        colors: { background: DEFAULT_BACKGROUND, foreground: WHITE, label: WHITE },
        logoText: content.title,
    };
    if (style === 'boardingPass') {
        const flightNumber = `${flight.carrier} ${flight.number}`.trim();
        return {
            style,
            images: { ...chrome.images },
            colors: { ...chrome.colors },
            logoText: chrome.logoText,
            transitType: 'PKTransitTypeAir',
            fields: {
                header: [{ key: 'flight', label: 'FLIGHT', value: flightNumber }],
                primary: [
                    { key: 'origin', label: flight.departureAirport, value: flight.departureAirport },
                    { key: 'destination', label: flight.arrivalAirport, value: flight.arrivalAirport },
                ],
                secondary: serviceFields.slice(0, 1),
                // Live trio — the SAME conventional keys applyEventPatchToDesign and
                // applyLiveData key off. gate/status VALUES are event-updated on the
                // pass face; departure's VALUE is mint-refresh only (schedule changes
                // track via semantics.currentDepartureDate, not this field).
                //
                // back gets the REST of the pool (slice(1)) — Apple requires field
                // keys unique across the WHOLE pass, and repeating serviceFields[0]
                // here made every mint invalid on-device ("more than one field has
                // the key 'lounge'", 2026-08-28).
                // changeMessage → Apple shows a lock-screen banner on value change;
                // gate uses the labelled 'Gate: %@' copy (a bare '%@' would push a
                // context-free "A12" banner) — same copy as the VM adapter.
                auxiliary: [
                    {
                        key: 'departure',
                        label: 'Departure',
                        // On the ORIGIN airport's clock — where the passenger boards.
                        value: (0, airportTimeZones_js_1.formatAirportLocalDateTime)(flight.scheduledDeparture, flight.departureAirport),
                        changeMessage: '%@',
                    },
                    { key: 'gate', label: 'Gate', value: flight.gate ?? '—', changeMessage: 'Gate: %@' },
                    {
                        key: 'status',
                        label: 'Status',
                        value: (0, flightStatusText_js_1.flightStatusText)('ON_TIME', 0),
                        changeMessage: '%@',
                    },
                ],
                back: serviceFields.slice(1),
            },
            semantics: seedSemantics(flight),
            relevance: seedRelevance(flight, style),
        };
    }
    // Non-boardingPass styles: service title is the primary, the remaining
    // service fields land in back (the first one is already in secondary).
    return {
        style,
        images: { ...chrome.images },
        colors: { ...chrome.colors },
        logoText: chrome.logoText,
        fields: {
            primary: [{ key: 'title', value: content.title }],
            secondary: serviceFields.slice(0, 1),
            // Same pass-wide-unique-keys rule as the boardingPass branch above.
            back: serviceFields.slice(1),
        },
        relevance: seedRelevance(flight, style),
    };
}
//# sourceMappingURL=serviceToDesign.js.map